import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Banknote, AlertTriangle } from 'lucide-react';
import RevenueChart from './finance/RevenueChart';
import RevenueSummaryCards from './finance/RevenueSummaryCards';
import RevenueStructureChart from './finance/RevenueStructureChart';
import PaymentCycleCard from './finance/PaymentCycleCard';
import ArrearsTrendCharts from './finance/ArrearsTrendCharts';
import ArrearsList from './finance/ArrearsList';
import AnomalyCards from './finance/AnomalyCards';

/**
 * Finance Analytics Section
 * Contains two subsections:
 * 1. Przychody placówki (Revenue)
 * 2. Zaległości i Anomalie (Arrears & Anomalies)
 */
export default function FinanceSection({ branchId }) {
  const currentYear = new Date().getFullYear();
  const [filterMode, setFilterMode] = useState('previous_month'); // 'previous_month' or 'ytd'
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Calculate date range based on filter mode (use local dates to avoid UTC shift)
  const getDateRange = () => {
    if (filterMode === 'ytd') {
      return {
        startDate: `${selectedYear}-01-01`,
        endDate: `${selectedYear}-12-31`
      };
    } else {
      // Previous month: first and last day of last month in local time
      const today = new Date();
      const y = today.getFullYear();
      const m = today.getMonth() - 1; // 0-indexed, so previous month
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      const pad = (n) => String(n).padStart(2, '0');
      return {
        startDate: `${first.getFullYear()}-${pad(first.getMonth() + 1)}-${pad(first.getDate())}`,
        endDate: `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`
      };
    }
  };

  const { startDate, endDate } = getDateRange();

  // Generate year options (current year and 2 previous years)
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="space-y-6">
      {/* Section Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Finanse</h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filter Toggle */}
          <Tabs value={filterMode} onValueChange={setFilterMode} className="w-auto">
            <TabsList className="h-9 transition-all duration-300">
              <TabsTrigger value="previous_month" className="text-xs px-3 transition-all duration-300">
                Poprzedni miesiąc
              </TabsTrigger>
              <TabsTrigger value="ytd" className="text-xs px-3 transition-all duration-300">
                Rok
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Year Selector (only visible for YTD mode) */}
          {filterMode === 'ytd' && (
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Revenue Subsection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-emerald-600" />
          <h3 className="font-medium text-slate-700">Przychody placówki</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Revenue Summary Cards */}
          <RevenueSummaryCards
            branchId={branchId}
            year={selectedYear}
            filterMode={filterMode}
          />

          {/* Payment Cycle Card */}
          <PaymentCycleCard
            branchId={branchId}
            startDate={startDate}
            endDate={endDate}
          />
        </div>

        {/* Revenue Chart - Stacked Bar */}
        <RevenueChart
          branchId={branchId}
          year={selectedYear}
        />

        {/* Revenue Structure Chart */}
        <RevenueStructureChart
          branchId={branchId}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

      {/* Arrears & Anomalies Subsection */}
      <div className="space-y-4 pt-4 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <h3 className="font-medium text-slate-700">Zaległości i Anomalie</h3>
        </div>

        {/* Anomaly Alert Cards */}
        <AnomalyCards branchId={branchId} />

        {/* Arrears Trend Charts */}
        <ArrearsTrendCharts
          branchId={branchId}
          year={selectedYear}
        />

        {/* Arrears List with Actions */}
        <ArrearsList branchId={branchId} />
      </div>
    </div>
  );
}
